import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { HistoricDataLoader, RowField, RowIdField } from '@thinkalpha/table-client';
import type {
    Bounds,
    FieldDescriptor,
    RawClient,
    RowUpdate,
    RowUpdateSnapshotBatch,
    SortModel,
    TimePoint,
} from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { BehaviorSubject, Subject } from 'rxjs';
import type { Observable, Unsubscribable } from 'rxjs';
import { bufferTime, filter } from 'rxjs/operators';
import type { TableModelGridSourceModel } from 'src/features/table/next/tableModelGridSource';
import { NEW_TABLE_RESET } from 'src/models/TableModel';

/** The subset of `HistoricDataLoader` the model drives; structural so tests can fake the wire. */
export interface HistoricLoaderLike {
    readonly point$: Observable<TimePoint>;
    readonly descriptors$: Observable<FieldDescriptor[]>;
    readMore(count?: number): void;
}

export interface HistoricTableModelDeps {
    client: RawClient;
    key: string;
    log: Logger;
    /** Test seam; defaults to a real `HistoricDataLoader` on the client. */
    createLoader?: (client: RawClient, key: string, log: Logger) => HistoricLoaderLike;
}

/** Points requested per "load more"; the legacy panel's page size. */
export const HISTORIC_PAGE_SIZE = 100;
/** Points arrive one per message; batch them so the grid sees one commit per burst. */
const POINT_BUFFER_MS = 50;
/** A page request that delivers nothing (end of history) clears the loading state after this long. */
export const HISTORIC_LOAD_SETTLE_MS = 3000;

type NumberedPoint = TimePoint & { readonly [POINT_ID]: number };
const POINT_ID = Symbol('historicPointId');

function compareValues(a: unknown, b: unknown): number {
    if (a === b) return 0;
    if (a === undefined || a === null) return 1;
    if (b === undefined || b === null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a) < String(b) ? -1 : 1;
}

/**
 * Historic (`:H`) tables are paged, not streamed: the loader delivers points on request and the
 * panel appends them to a local array. This model publishes that array through the same
 * `TableModelGridSourceModel` contract the live panels use, so one grid host serves both. Each
 * change republishes the active window after a generation reset (AC-STREAM-018 semantics on a
 * consumer-owned ordered array); sort is applied locally.
 */
export class HistoricTableModel implements TableModelGridSourceModel, Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #points = signal<readonly NumberedPoint[]>([]);
    readonly #descriptors = signal<readonly FieldDescriptor[]>([]);
    readonly #sort = signal<SortModel | null>(null);
    readonly #loading = signal(false);
    readonly #updates = new Subject<RowUpdateSnapshotBatch | typeof NEW_TABLE_RESET>();
    readonly #count = new BehaviorSubject(0);
    readonly #loader: HistoricLoaderLike;
    readonly #key: string;
    readonly #log: Logger;
    #loadSettleTimer: ReturnType<typeof setTimeout> | undefined;
    #bounds: Bounds = { firstRow: 0, windowSize: 0 };
    #nextPointId = 0;

    readonly descriptors: ReadonlySignal<readonly FieldDescriptor[]>;
    readonly rowCount: ReadonlySignal<number>;
    readonly loading: ReadonlySignal<boolean>;
    readonly orderedPoints: ReadonlySignal<readonly NumberedPoint[]>;

    constructor(deps: HistoricTableModelDeps) {
        this.#loader = (deps.createLoader ?? ((client, key, log) => new HistoricDataLoader(client, key, log)))(
            deps.client,
            deps.key,
            deps.log,
        );
        this.#key = deps.key;
        this.#log = deps.log;
        this.descriptors = this.#descriptors;
        this.loading = this.#loading;
        this.rowCount = computed(() => this.#points.value.length);
        this.orderedPoints = computed(() => {
            const sort = this.#sort.value;
            const points = this.#points.value;
            if (sort === null) return points;
            const entries = Object.entries(sort);
            return [...points].sort((a, b) => {
                for (const [field, direction] of entries) {
                    const order = compareValues(a[field], b[field]);
                    if (order !== 0) return direction === 'ascending' ? order : -order;
                }
                return 0;
            });
        });

        const subscriptions: Unsubscribable[] = [
            this.#loader.descriptors$.subscribe((descriptors) => {
                this.#descriptors.value = descriptors;
            }),
            this.#loader.point$
                .pipe(
                    bufferTime(POINT_BUFFER_MS),
                    filter((batch) => batch.length > 0),
                )
                .subscribe((batch) => {
                    const numbered = batch.map((point): NumberedPoint => {
                        const id = this.#nextPointId;
                        this.#nextPointId += 1;
                        return Object.assign({ ...point }, { [POINT_ID]: id });
                    });
                    if (TRACE) {
                        this.#log.trace({
                            message: 'Historic points received',
                            key: this.#key,
                            count: numbered.length,
                        });
                    }
                    this.#points.value = [...this.#points.value, ...numbered];
                    this.#settleLoading();
                }),
        ];
        this.#disposableStack.defer(() => {
            for (const subscription of subscriptions) subscription.unsubscribe();
        });
        // Any change to the ordered rows is a new generation for the grid: reset, then republish
        // the window. Bounds changes alone republish without a reset.
        this.#disposableStack.defer(
            effect(() => {
                const ordered = this.orderedPoints.value;
                this.#count.next(ordered.length);
                this.#updates.next(NEW_TABLE_RESET);
                this.#publishWindow(ordered);
            }),
        );
        this.#disposableStack.defer(() => {
            clearTimeout(this.#loadSettleTimer);
            this.#updates.complete();
            this.#count.complete();
        });
    }

    get updatesWithReset$(): Observable<RowUpdateSnapshotBatch | typeof NEW_TABLE_RESET> {
        return this.#updates;
    }

    get count$(): Observable<number> {
        return this.#count;
    }

    setBounds(bounds: Bounds): void {
        if (bounds.firstRow === this.#bounds.firstRow && bounds.windowSize === this.#bounds.windowSize) return;
        this.#bounds = bounds;
        this.#publishWindow(this.orderedPoints.value);
    }

    setSort(sort: SortModel | null): void {
        this.#sort.value = sort === null || Object.keys(sort).length === 0 ? null : sort;
    }

    loadMore(count = HISTORIC_PAGE_SIZE): void {
        if (TRACE) {
            this.#log.trace({ message: 'Requesting historic points', key: this.#key, count });
        }
        this.#loading.value = true;
        // The loader has no end-of-data signal; an empty page simply delivers nothing. Settle the
        // loading state after a bounded wait so the panel never shows "Loading…" forever.
        clearTimeout(this.#loadSettleTimer);
        this.#loadSettleTimer = setTimeout(() => this.#settleLoading(), HISTORIC_LOAD_SETTLE_MS);
        this.#loader.readMore(count);
    }

    #settleLoading(): void {
        clearTimeout(this.#loadSettleTimer);
        this.#loadSettleTimer = undefined;
        this.#loading.value = false;
    }

    #publishWindow(ordered: readonly NumberedPoint[]): void {
        const { firstRow, windowSize } = this.#bounds;
        const end = Math.min(firstRow + windowSize, ordered.length);
        if (firstRow >= end) return;
        const updates: RowUpdate[] = [];
        for (let index = firstRow; index < end; index += 1) {
            const point = ordered[index];
            updates.push({ ...point, [RowField]: index, [RowIdField]: point[POINT_ID] });
        }
        this.#updates.next(Object.assign(updates, { type: 'snapshot' as const }));
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
